import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "~/api/provider";

/**
 * BRIEF-CGNI S2-2 — budgets by context (`budgets.*`, `{ok, data}`). Who pays is the server's to resolve; these types only carry
 * what it said: each payer's plan entitlement, the admin's allocation, what was used and held this month and today, whether
 * the limit is enforced or in shadow, and the real reset.
 */

export type BudgetAccount = {
  kind: "workspace" | "project" | "person";
  subjectId: string;
  name: string;
  mine: boolean;
  entitlement: number | null;
  allocation: number | null;
  dailyLimit: number | null;
  limit: number | null;
  enforced: boolean;
  used: number;
  held: number;
  today: number;
  calls: number;
  resetsAt: string;
  dailyResetsAt: string | null;
  state: "ok" | "near" | "exhausted" | "none";
  wouldHaveWaited: string | null;
  setBy: string | null;
  setByName: string | null;
  setAt: string | null;
};

export type BudgetUsage = {
  month: string;
  resetsAt: string;
  plan: string | null;
  perSeat: number | null;
  seats: number;
  pooled: boolean;
  admin: boolean;
  requireContext: boolean;
  workspace: BudgetAccount;
  projects: BudgetAccount[];
  people: BudgetAccount[];
  withoutContext: { source: string; lane: string | null; tokens: number; calls: number }[];
  uncertain: number;
  uncertainTokens: number;
  refusals: {
    at: string;
    kind: string;
    subjectId: string | null;
    sentence: string;
    enforced: boolean;
  }[];
};

export type BudgetSet = {
  kind: BudgetAccount["kind"];
  subjectId: string;
  monthlyLimit: number | null;
  dailyLimit: number | null;
  enforce: boolean;
};

type Envelope<T> = { ok: boolean; data: T; message?: string; error?: string };

const KEY = ["thinkers", "budgets"] as const;

export function useBudgets() {
  const api = useApi();
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const response = (await api.research.post("budgets.usage", {})) as Envelope<BudgetUsage>;
      if (!response.ok) throw new Error(response.message ?? response.error ?? "budgets.usage");
      return response.data;
    },
    refetchInterval: 60_000,
  });
}

export function useSetBudget() {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: BudgetSet) => {
      const response = (await api.research.post("budgets.set", body)) as Envelope<BudgetUsage>;
      if (!response.ok) throw new Error(response.message ?? response.error ?? "budgets.set");
      return response.data;
    },
    onSuccess: (data) => client.setQueryData(KEY, data),
  });
}

/** "" ⇒ null (no limit of the admin's); otherwise a whole number of tokens, 0 included. */
export function parseLimit(text: string): number | null | "invalid" {
  const trimmed = text.replace(/[\s,]/g, "");
  if (trimmed === "") return null;
  if (!/^\d+$/.test(trimmed)) return "invalid";
  return Number(trimmed);
}
